import React, { useState, useEffect, useMemo } from 'react';
import { getHandlerRequestsByBreederId, getHandlerRequestsByHandlerId, sendHandlerRequest, updateHandlerRequest, updateHandlerRequestStatus } from '../api/actions';
import { useParticipants } from '../hooks/useParticipants';
import { getAdminRotationRequests, updateParticipantAsAdmin, createParticipantAsAdmin, DeleteParticipant } from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import { calculateAvailableSlotsForBreeder, getHandlerCapacitySummary } from '../utils/handlerCapacity';

const BIRD_STATUS_OPTIONS = ['active', 'injured', 'lost', 'died'];
const BIRD_SEX_OPTIONS = ['Hen', 'Cock'];
const REQUEST_STATUS_OPTIONS = ['approved', 'pending', 'rejected', 'cancelled', 'deleted'];

const normalizeBirdCondition = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return BIRD_STATUS_OPTIONS.includes(normalized) ? normalized : 'active';
};

const normalizeBirdSexOption = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'h' || normalized === 'hen' || normalized === 'female' || normalized === 'f') return 'Hen';
  if (normalized === 'c' || normalized === 'cock' || normalized === 'male' || normalized === 'm') return 'Cock';
  return '';
};

const createPlaceholderBirdDetails = (numberOfBirds) =>
  Array.from({ length: Math.max(0, Number(numberOfBirds) || 0) }, () => ({
    bandNumber: '[To be added]',
    org: '',
    serialNumber: '',
    clubCode: '',
    year: '',
    color: '[To be added]',
    sex: '[To be added]',
    status: 'placeholder',
    birdStatus: 'active'
  }));

const getRequiredAdminRotationBirds = (totalBirds) => {
  const normalizedTotalBirds = Number(totalBirds) || 0;
  if (normalizedTotalBirds >= 30) return 3;
  if (normalizedTotalBirds >= 12) return 2;
  if (normalizedTotalBirds >= 5) return 1;
  return 0;
};

const buildMandatoryRotationHandlerRequest = (rotationRequest, handlerId, birdsToAssign) => ({
  breederId: rotationRequest.breederId,
  handlerId,
  numberOfBirds: birdsToAssign,
  birdsRequested: birdsToAssign,
  birdDetails: createPlaceholderBirdDetails(birdsToAssign),
  status: 'approved',
  id: `${rotationRequest.breederId}_${handlerId}`,
  requestDate: new Date().toISOString(),
  cancelledAt: null,
  cancelledBy: null,
  rejectionReason: null,
  requestNote: 'admin-rotation-assigned',
  isMandatoryRotation: true,
  IsMandatoryRotation: true
});

const getRequestsFromSource = (source) => {
  if (Array.isArray(source)) return source;
  return source && typeof source === 'object' ? [source] : [];
};

const flattenParticipantRequests = (participants = []) => {
  const seen = new Set();

  const extractRequests = (source) => {
    if (!source) return [];

    if (Array.isArray(source)) {
      return source.flatMap((item) => {
        if (!item || typeof item !== 'object') return [];
        if (item.handlerRequestData || item.HandlerRequestData || item.requestData || item.RequestData) {
          return [item.handlerRequestData || item.HandlerRequestData || item.requestData || item.RequestData];
        }
        return [item];
      });
    }

    if (typeof source === 'object') {
      if (source.handlerRequestData || source.HandlerRequestData || source.requestData || source.RequestData) {
        return [source.handlerRequestData || source.HandlerRequestData || source.requestData || source.RequestData];
      }
      return [source];
    }

    return [];
  };

  return participants.flatMap((participant) => {
    const sources = [
      participant?.handlerRequest,
      participant?.handlerRequests,
      participant?.HandlerRequest,
      participant?.HandlerRequests,
      participant?.handlerRequestData,
      participant?.HandlerRequestData,
      participant?.requestData,
      participant?.RequestData,
      participant?.handlerInfo?.handlerRequest,
      participant?.handlerInfo?.handlerRequests,
      participant?.handlerInfo?.HandlerRequest,
      participant?.handlerInfo?.HandlerRequests,
      participant?.breederInfo?.handlerRequest,
      participant?.breederInfo?.handlerRequests,
      participant?.breederInfo?.HandlerRequest,
      participant?.breederInfo?.HandlerRequests,
      participant?.breederInfo?.handlerRequestData,
      participant?.breederInfo?.HandlerRequestData,
      participant?.breederInfo?.requestData,
      participant?.breederInfo?.RequestData
    ];

    return sources.flatMap(extractRequests);
  }).filter((request, index) => {
    const normalizedRequest = request?.handlerRequestData || request?.HandlerRequestData || request?.requestData || request?.RequestData || request;
    const key = normalizedRequest?.id
      || normalizedRequest?.requestId
      || normalizedRequest?.RequestId
      || `${normalizedRequest?.breederId || normalizedRequest?.BreederId || 'breeder'}-${normalizedRequest?.handlerId || normalizedRequest?.HandlerId || 'handler'}-${index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return Boolean(normalizedRequest);
  });
};

function AdminBirdInfoModal({ show, request, onSaveBirds, onSaveRequest, onClose, getHandlerName, availableHandlers }) {
  const [draft, setDraft] = useState(null);

  const getRequestLevelStatus = (requestData) => String(
    requestData?.status
    ?? requestData?.Status
    ?? requestData?.requestStatus
    ?? requestData?.RequestStatus
    ?? 'pending'
  ).trim().toLowerCase();

  const normalizeRequestLevelStatus = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return REQUEST_STATUS_OPTIONS.includes(normalized) ? normalized : 'pending';
  };

  const createEmptyBird = () => ({
    bandNumber: '',
    org: '',
    serialNumber: '',
    clubCode: '',
    year: '',
    color: '',
    sex: '',
    birdStatus: 'active',
    status: 'active'
  });

  const normalizeBirdDraft = (bird = createEmptyBird()) => {
    const normalizedStatus = normalizeBirdCondition(bird?.birdStatus ?? bird?.status);
    return {
      ...bird,
      sex: normalizeBirdSexOption(bird?.sex ?? bird?.Sex ?? bird?.gender ?? bird?.Gender ?? bird?.birdSex ?? bird?.BirdSex),
      birdStatus: normalizedStatus,
      status: normalizedStatus,
    };
  };

  const syncBirdCounts = (requestDraft, birdDetails) => {
    const count = Array.isArray(birdDetails) ? birdDetails.length : 0;
    return {
      ...requestDraft,
      birdDetails,
      numberOfBirds: count,
      birdsRequested: count,
    };
  };

  const createDraftFromRequest = (requestData) => {
    if (!requestData) return null;

    const sourceBirds = Array.isArray(requestData?.birdDetails) ? requestData.birdDetails : [];
    const requestedCount = Math.max(
      sourceBirds.length,
      Number(requestData?.numberOfBirds ?? requestData?.birdsRequested ?? 0) || 0
    );

    const normalizedBirds = Array.from({ length: requestedCount }, (_, index) => {
      const bird = sourceBirds[index] || createEmptyBird();
      return normalizeBirdDraft(bird);
    });

    const nextDraft = {
      ...requestData,
      handlerId: requestData?.handlerId || '',
      status: normalizeRequestLevelStatus(getRequestLevelStatus(requestData)),
      birdDetails: normalizedBirds,
    };

    return syncBirdCounts(nextDraft, normalizedBirds);
  };

  useEffect(() => {
    if (!request) {
      setDraft(null);
      return;
    }

    setDraft(createDraftFromRequest(request));
  }, [request]);

  if (!show || !request) return null;

  const draftBirds = Array.isArray(draft?.birdDetails) ? draft.birdDetails : [];

  const handleBirdChange = (idx, field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;

      const nextBirds = (Array.isArray(prev.birdDetails) ? prev.birdDetails : []).map((bird, index) => {
        if (index !== idx) return bird;

        if (field === 'birdStatus') {
          const normalizedStatus = normalizeBirdCondition(value);
          return {
            ...bird,
            birdStatus: normalizedStatus,
            status: normalizedStatus,
          };
        }

        if (field === 'sex') {
          return {
            ...bird,
            sex: normalizeBirdSexOption(value),
          };
        }

        return {
          ...bird,
          [field]: String(value || '').toUpperCase(),
        };
      });

      return syncBirdCounts({ ...prev, birdDetails: nextBirds }, nextBirds);
    });
  };

  const handleAddBird = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const nextBirds = [...(Array.isArray(prev.birdDetails) ? prev.birdDetails : []), createEmptyBird()];
      return syncBirdCounts({ ...prev, birdDetails: nextBirds }, nextBirds);
    });
  };

  const handleRemoveBird = (idx) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const nextBirds = (Array.isArray(prev.birdDetails) ? prev.birdDetails : []).filter((_, i) => i !== idx);
      return syncBirdCounts({ ...prev, birdDetails: nextBirds }, nextBirds);
    });
  };

  const handleSave = async () => {
    if (!draft) return;

    const normalizedBirds = (Array.isArray(draft.birdDetails) ? draft.birdDetails : []).map((bird) => normalizeBirdDraft(bird));
    const normalizedDraft = syncBirdCounts(
      {
        ...draft,
        status: normalizeRequestLevelStatus(draft?.status),
        birdDetails: normalizedBirds,
      },
      normalizedBirds
    );

    if (onSaveRequest) {
      await onSaveRequest(request.id, normalizedDraft);
    } else {
      await onSaveBirds(request.id, normalizedBirds);
    }
  };

const handleSoftDeleteRequest = async () => {
  if (!draft) return;

  const confirmed = window.confirm(
    'Are you sure you want to reject this handler request? This will mark it as rejected and hide it from active lists.'
  );
  if (!confirmed) return;

  const requestId = draft.id || request.id;
  const result = await updateHandlerRequestStatus(requestId, 'rejected');

  if (result.success) {
    onClose && onClose(true);
  }
};

  const breederLabel = [
    request.breederName,
    request.breederLoftName ? `(${request.breederLoftName})` : ''
  ].filter(Boolean).join(' ') || 'Unknown Breeder';
  const requestTypeLabel = (request.isMandatoryRotation || request.IsMandatoryRotation)
    ? 'Mandatory Rotation'
    : 'Standard';

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl" onClick={(event) => event.stopPropagation()} style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
        <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2"></i>
              Manage Handler Request
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>

            {/* Compact Request Summary */}
            <div className="row g-3 mb-4 pb-3 border-bottom">
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block fw-bold mb-1">Breeder</small>
                <div className="fw-semibold">{breederLabel}</div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block fw-bold mb-1">Birds</small>
                <div className="fw-semibold">{draftBirds.length}</div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block fw-bold mb-1">Status</small>
                <div className="fw-semibold text-capitalize">
                  {normalizeRequestLevelStatus(draft?.status)}
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-muted d-block fw-bold mb-1">Type</small>
                <span className={`badge ${requestTypeLabel === 'Mandatory Rotation' ? 'bg-primary' : 'bg-secondary'}`}>
                  {requestTypeLabel}
                </span>
              </div>
            </div>

            {/* Handler Selector Section */}
            <div className="mb-4">
              <label className="form-label fw-bold mb-2">
                <i className="bi bi-person-badge me-1"></i>
                Assigned Handler
              </label>
              {availableHandlers && availableHandlers.length > 0 ? (
                <select
                  className="form-select"
                  value={draft?.handlerId || ''}
                  onChange={e => setDraft((prev) => (prev ? { ...prev, handlerId: e.target.value } : prev))}
                >
                  <option value="">Select handler...</option>
                  {availableHandlers.map(h => (
                    <option key={h.id} value={h.id}>
                      {h.fullName}{h.loftName ? ` (${h.loftName})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="form-control-plaintext text-muted">{getHandlerName(request)}</div>
              )}
            </div>

            {/* Birds Section - Main Focus */}
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="bi bi-list-ul me-2"></i>
                Birds ({draftBirds.length})
              </h5>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleAddBird}
                title="Add a new bird to this request"
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Bird
              </button>
            </div>

            {draftBirds.length === 0 && (
              <div className="text-center text-muted py-4 border rounded">
                <i className="bi bi-inbox" style={{fontSize: '2rem'}}></i>
                <p className="mt-2 mb-0">No birds in this request. Click "Add Bird" to add one.</p>
              </div>
            )}

            {/* Desktop table */}
            {draftBirds.length > 0 && (
              <div className="d-none d-md-block mb-4 table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th style={{width: '40px'}}>#</th>
                      <th>Band Number</th>
                      <th>Color</th>
                      <th>Sex</th>
                      <th>Status</th>
                      <th style={{width: '60px'}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftBirds.map((bird, idx) => (
                      <tr key={idx}>
                        <td className="text-muted align-middle fw-semibold">{idx + 1}</td>
                        <td className="align-middle">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={bird.bandNumber || ''}
                            onChange={e => handleBirdChange(idx, 'bandNumber', e.target.value)}
                            placeholder="Band number"
                          />
                        </td>
                        <td className="align-middle">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={bird.color || ''}
                            onChange={e => handleBirdChange(idx, 'color', e.target.value)}
                            placeholder="Color"
                          />
                        </td>
                        <td className="align-middle">
                          <select
                            className="form-select form-select-sm"
                            value={normalizeBirdSexOption(bird.sex)}
                            onChange={e => handleBirdChange(idx, 'sex', e.target.value)}
                          >
                            <option value="">Sex</option>
                            {BIRD_SEX_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td className="align-middle">
                          <select
                            className="form-select form-select-sm"
                            value={normalizeBirdCondition(bird.birdStatus ?? bird.status)}
                            onChange={e => handleBirdChange(idx, 'birdStatus', e.target.value)}
                          >
                            {BIRD_STATUS_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="align-middle text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveBird(idx)}
                            title="Remove this bird"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile cards */}
            {draftBirds.length > 0 && (
              <div className="d-md-none mb-4">
                {draftBirds.map((bird, idx) => (
                  <div key={idx} className="card mb-3">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Bird #{idx + 1}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveBird(idx)}
                        title="Remove this bird"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label small fw-bold">Band Number</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={bird.bandNumber || ''}
                          onChange={e => handleBirdChange(idx, 'bandNumber', e.target.value)}
                          placeholder="Band number"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-bold">Color</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={bird.color || ''}
                          onChange={e => handleBirdChange(idx, 'color', e.target.value)}
                          placeholder="Color"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-bold">Sex</label>
                        <select
                          className="form-select form-select-sm"
                          value={normalizeBirdSexOption(bird.sex)}
                          onChange={e => handleBirdChange(idx, 'sex', e.target.value)}
                        >
                          <option value="">Select sex</option>
                          {BIRD_SEX_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-0">
                        <label className="form-label small fw-bold">Status</label>
                        <select
                          className="form-select form-select-sm"
                          value={normalizeBirdCondition(bird.birdStatus ?? bird.status)}
                          onChange={e => handleBirdChange(idx, 'birdStatus', e.target.value)}
                        >
                          {BIRD_STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>
                              {opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer bg-light">
            <div className="d-flex gap-2 justify-content-between w-100">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleSoftDeleteRequest}
              >
                <i className="bi bi-trash me-1"></i>
                Reject Request
              </button>
              <div className="d-flex gap-2">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSave}
              >
                <i className="bi bi-check-circle me-1"></i>
                Save Changes
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminTab({ 
  participant, 
  onMessage, 
  onParticipantUpdate, 
  isEditing, 
  onEditingChange, 
  saving, 
  setSaving 
}) {
  const getRequestStatusValue = (request) => (
    request?.status
    ?? request?.Status
    ?? request?.requestStatus
    ?? request?.RequestStatus
    ?? request?.handlerRequestData?.status
    ?? request?.handlerRequestData?.Status
    ?? request?.HandlerRequestData?.status
    ?? request?.HandlerRequestData?.Status
    ?? ''
  );

  const normalizeRequestStatus = (value) => String(value || '').trim().toLowerCase();

  const [adminRotationRequests, setAdminRotationRequests] = useState([]);
  // State for handler requests received for selected participant
  const [adminHandlersRequestsReceived, setAdminHandlersRequestsReceived] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsByHandlerId, setRequestsByHandlerId] = useState({});
  const [selectedRotationBirdHandlers, setSelectedRotationBirdHandlers] = useState({});
  const [showBirdInfoModal, setShowBirdInfoModal] = useState(false);
  const [selectedBirdRequest, setSelectedBirdRequest] = useState(null);
  const [selectedParticipantId, setSelectedParticipantId] = useState('');
  const [isNewParticipant, setIsNewParticipant] = useState(false);
  const [participantFormData, setParticipantFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingParticipant, setDeletingParticipant] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const { participants: allParticipants, loadParticipants } = useParticipants();

  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  const getHandlerDisplayName = (request) => {
    const handler = allParticipants?.find(p => p.id === request?.handlerId);
    if (!handler) return request?.handlerId || 'Unknown handler';
    return handler.loftName ? `${handler.fullName} (${handler.loftName})` : handler.fullName;
  };

  const getParticipantRoleFlags = (person) => {
    const roles = Array.isArray(person?.role)
      ? person.role.map(role => String(role).toLowerCase())
      : [String(person?.role || '').toLowerCase()].filter(Boolean);

    return {
      isHandler: roles.includes('handler'),
      isBreeder: roles.includes('breeder')
    };
  };


  // Calculate approved birds and open slots for handler
  const approvedBirds = useMemo(() => {
    return adminHandlersRequestsReceived
      .filter(req => normalizeRequestStatus(getRequestStatusValue(req)) === 'approved')
      .reduce((sum, req) => sum + (parseInt(req.numberOfBirds || req.birdsRequested) || 0), 0);
  }, [adminHandlersRequestsReceived]);

  const openSlots = (participantFormData.handlerInfo?.totalBirds || 0) - approvedBirds;

  // Load admin rotation requests
  const loadAdminRotationRequests = async () => {
    if (!hasRole('admin')) {
      return;
    }
    
    try {
      setLoadingRequests(true);
      const result = await getAdminRotationRequests();
      
      if (result.success) {
        setAdminRotationRequests(result.requests || []);
        onMessage(`Loaded ${result.requests?.length || 0} admin rotation requests`, 'success');
      } else {
        onMessage(`Failed to load rotation requests: ${result.message}`, 'error');
      }
    } catch (error) {
      onMessage('Error loading rotation requests', 'error');
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (hasRole('admin')) {
      loadAdminRotationRequests();
    }
  }, [participant]);

  // Global save integration
  useEffect(() => {
    window.adminTabSave = async () => {
      try {
        setSaving(true);
        // Admin tab doesn't have traditional save operations like other tabs
        // But we can refresh data to ensure it's current
        await loadAdminRotationRequests();
        onMessage('Admin data refreshed successfully');
      } catch (error) {
        onMessage('Error refreshing admin data');
        throw error;
      } finally {
        setSaving(false);
        if (onEditingChange) {
          onEditingChange(false);
        }
      }
    };

    return () => {
      delete window.adminTabSave;
    };
  }, [setSaving, onEditingChange, onMessage]);

  // Filter actionable rotation requests and enrich with breeder information
  const pendingRotationRequests = useMemo(() => {
    const actionableRequests = adminRotationRequests.filter((request) => {
      const totalBirds = Number(request?.numberOfBirds || request?.birdsRequested || 0);
      const assignedBirds = Number(request?.assignedBirds || 0);
      const remainingBirds = Math.max(0, totalBirds - assignedBirds);
      const handlerId = String(request?.handlerId || '').toLowerCase();
      const isRotationRequest = handlerId === 'admin-rotation' || handlerId === 'admin-rotation-mandatory' || request?.isAdminRotation === true || request?.isMandatoryRotation === true || request?.IsMandatoryRotation === true;

      return isRotationRequest && remainingBirds > 0;
    });

    // Enrich with participant data
    return actionableRequests.map(request => {
      const breeder = allParticipants?.find(p => p.id === request.breederId);
      
      if (breeder) {
        return {
          ...request,
          breederName: breeder.fullName,
          breederLoftName: breeder.loftName,
          breederEmail: breeder.emailAddress,
          breederMobile: breeder.mobile
        };
      }
      
      return request;
    });
  }, [adminRotationRequests, allParticipants]);

  // Get available handlers for assignment
  const availableHandlers = useMemo(() => {
    if (!allParticipants) return [];
    
    const handlers = allParticipants.filter(p => {
      const hasHandlerRole = Array.isArray(p.role) 
        ? p.role.some(r => r.toLowerCase() === 'handler')
        : p.role?.toLowerCase() === 'handler';
      
      // Include all handlers, not just those with openSlots > 0
      // Let the modal show capacity details and let admin decide
      return hasHandlerRole;
    });
    
    return handlers;
  }, [allParticipants]);

  useEffect(() => {
    if (!hasRole('admin') || availableHandlers.length === 0) {
      setRequestsByHandlerId({});
      return;
    }

    let isCancelled = false;

    const loadHandlerRequests = async () => {
      try {
        const results = await Promise.all(
          availableHandlers.map(async (handler) => {
            try {
              const result = await getHandlerRequestsByHandlerId(handler.id);
              return [handler.id, result?.success ? (result.requests || []) : []];
            } catch (error) {
              return [handler.id, []];
            }
          })
        );

        if (!isCancelled) {
          setRequestsByHandlerId(Object.fromEntries(results));
        }
      } catch (error) {
        if (!isCancelled) {
          setRequestsByHandlerId({});
        }
      }
    };

    loadHandlerRequests();

    return () => {
      isCancelled = true;
    };
  }, [availableHandlers, participant]);

  const authoritativeHandlerRequests = useMemo(
    () => Object.values(requestsByHandlerId || {}).flatMap(getRequestsFromSource),
    [requestsByHandlerId]
  );

  const allKnownRequests = useMemo(
    () => {
      const merged = [
        ...flattenParticipantRequests(allParticipants || []),
        ...authoritativeHandlerRequests
      ];

      const seen = new Set();
      return merged.filter((request, index) => {
        const normalizedRequest = request?.handlerRequestData || request?.HandlerRequestData || request?.requestData || request?.RequestData || request;
        const key = normalizedRequest?.id
          || normalizedRequest?.requestId
          || normalizedRequest?.RequestId
          || `${normalizedRequest?.breederId || normalizedRequest?.BreederId || 'breeder'}-${normalizedRequest?.handlerId || normalizedRequest?.HandlerId || 'handler'}-${normalizedRequest?.requestDate || normalizedRequest?.RequestDate || 'date'}-${index}`;

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [allParticipants, authoritativeHandlerRequests]
  );

  const sortedHandlersForRotation = useMemo(() => {
    const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

    const isAssignedStatus = (value) => {
      const status = normalizeStatus(value);
      return status === 'approved' || status === 'assigned' || status === 'accepted' || status === 'complete' || status === 'completed';
    };

    const getRequestHandlerId = (request) => String(
      request?.handlerId
      || request?.HandlerId
      || request?.handlerID
      || request?.handlerRequestData?.handlerId
      || request?.HandlerRequestData?.HandlerId
      || ''
    ).trim().toLowerCase();

    const getRequestStatus = (request) => (
      request?.status
      || request?.Status
      || request?.requestStatus
      || request?.RequestStatus
      || request?.handlerRequestData?.status
      || request?.HandlerRequestData?.Status
      || ''
    );

    const requestsByHandler = new Map();
    allKnownRequests.forEach((request) => {
      const handlerId = getRequestHandlerId(request);
      if (!handlerId || !isAssignedStatus(getRequestStatus(request))) return;

      const existing = requestsByHandler.get(handlerId) || 0;
      requestsByHandler.set(handlerId, existing + 1);
    });

    return [...availableHandlers].sort((a, b) => {
      const summaryA = getHandlerCapacitySummary(a, allKnownRequests);
      const summaryB = getHandlerCapacitySummary(b, allKnownRequests);

      const requestsA = requestsByHandler.get(String(a?.id || '').trim().toLowerCase()) || 0;
      const requestsB = requestsByHandler.get(String(b?.id || '').trim().toLowerCase()) || 0;

      const aHasZeroRequests = requestsA === 0;
      const bHasZeroRequests = requestsB === 0;
      if (aHasZeroRequests !== bHasZeroRequests) {
        return aHasZeroRequests ? -1 : 1;
      }

      const utilizationA = summaryA.totalBirds > 0 ? (summaryA.approved / summaryA.totalBirds) : 1;
      const utilizationB = summaryB.totalBirds > 0 ? (summaryB.approved / summaryB.totalBirds) : 1;
      if (utilizationA !== utilizationB) {
        return utilizationA - utilizationB;
      }

      if (summaryA.openSlots !== summaryB.openSlots) {
        return summaryB.openSlots - summaryA.openSlots;
      }

      return String(a?.fullName || '').localeCompare(String(b?.fullName || ''));
    });
  }, [availableHandlers, allKnownRequests]);

  const adminVisibleHandlerRequests = useMemo(() => {
    return adminHandlersRequestsReceived
      .filter((request) => {
        const handlerId = String(request?.handlerId || '').toLowerCase();
        const status = normalizeRequestStatus(getRequestStatusValue(request));
        return Boolean(handlerId)
          && handlerId !== 'admin-rotation'
          && handlerId !== 'admin-rotation-mandatory'
          && status !== 'cancelled'
          && status !== 'rejected'
          && status !== 'deleted';
      })
      .map((request) => {
        const breeder = allParticipants?.find(p => p.id === request.breederId);
        const handler = allParticipants?.find(p => p.id === request.handlerId);

        return {
          ...request,
          status: getRequestStatusValue(request),
          breederName: breeder?.fullName || request?.breederName || 'Unknown breeder',
          breederLoftName: breeder?.loftName || '',
          handlerName: handler?.fullName || request?.handlerName || 'Unknown handler',
          handlerLoftName: handler?.loftName || '',
          birdDetails: Array.isArray(request?.birdDetails) ? request.birdDetails : [],
          isMandatoryRotation: request?.isMandatoryRotation === true || request?.IsMandatoryRotation === true
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.requestDate || 0).getTime();
        const dateB = new Date(b.requestDate || 0).getTime();
        return dateB - dateA;
      });
  }, [adminHandlersRequestsReceived, allParticipants]);

  const selectedParticipant = useMemo(
    () => allParticipants?.find(p => p.id === selectedParticipantId) || null,
    [allParticipants, selectedParticipantId]
  );

  const selectedParticipantRequestLabel = useMemo(() => {
    return 'Bird Assignments';
  }, [selectedParticipantId, selectedParticipant]);

  const selectedParticipantRotationBirdRows = useMemo(() => {
    if (!selectedParticipant) return [];

    const { isBreeder } = getParticipantRoleFlags(selectedParticipant);
    if (!isBreeder) return [];

    const totalBirds = Number(selectedParticipant?.breederInfo?.totalBirdsForRace || selectedParticipant?.totalBirds || 0);
    const requiredRotationBirds = getRequiredAdminRotationBirds(totalBirds);
    if (requiredRotationBirds <= 0) return [];

    const mandatoryAssignments = allKnownRequests
      .filter((request) => {
        const handlerId = String(request?.handlerId || '').toLowerCase();
        const isMandatoryRotation = request?.isMandatoryRotation === true || request?.IsMandatoryRotation === true;
        const isAdminRotationHandler = handlerId === 'admin-rotation' || handlerId === 'admin-rotation-mandatory';
        const birdsInRequest = Number(request?.numberOfBirds || request?.birdsRequested || 0);

        return request?.breederId === selectedParticipant.id
          && isMandatoryRotation
          && !isAdminRotationHandler
          && birdsInRequest > 0;
      })
      .sort((a, b) => {
        const dateA = new Date(a.requestDate || a.createDate || 0).getTime();
        const dateB = new Date(b.requestDate || b.createDate || 0).getTime();
        return dateA - dateB;
      });

    const assignmentBirds = mandatoryAssignments.flatMap((request) => {
      const birdsInRequest = Math.max(0, Number(request?.numberOfBirds || request?.birdsRequested || 0));
      const handler = allParticipants?.find((person) => person.id === request?.handlerId);
      const status = String(request?.status || 'pending').toLowerCase();

      return Array.from({ length: birdsInRequest }, (_, index) => ({
        assignmentIndex: index,
        requestId: request?.id || null,
        handlerId: request?.handlerId || '',
        handlerName: handler?.fullName || request?.handlerName || request?.handlerId || 'Unknown handler',
        requestDate: request?.requestDate || request?.createDate || null,
        status
      }));
    });

    const sourceRotationRequest = pendingRotationRequests.find((request) => request.breederId === selectedParticipant.id) || null;
    const requestDate = sourceRotationRequest?.requestDate || sourceRotationRequest?.createDate || new Date().toISOString();

    return Array.from({ length: requiredRotationBirds }, (_, index) => {
      const rotationBirdNumber = index + 1;
      const existingAssignment = assignmentBirds[index] || null;
      const assignmentStatus = String(existingAssignment?.status || '').toLowerCase();
      const rowStatus = !existingAssignment
        ? 'unassigned'
        : assignmentStatus === 'approved'
          ? 'approved'
          : 'pending';

      return {
        rowId: `${selectedParticipant.id}-rotation-rule-bird-${rotationBirdNumber}`,
        rowType: 'rotation-bird',
        breederId: selectedParticipant.id,
        breederName: selectedParticipant.fullName || 'Unknown breeder',
        breederLoftName: selectedParticipant.loftName || '',
        breederEmail: selectedParticipant.emailAddress || '',
        breederMobile: selectedParticipant.mobile || '',
        rotationBirdNumber,
        totalBirds,
        requiredRotationBirds,
        assignedBirds: assignmentBirds.length,
        remainingBirds: Math.max(0, requiredRotationBirds - assignmentBirds.length),
        sourceRotationRequestId: sourceRotationRequest?.id || null,
        requestDate: existingAssignment?.requestDate || requestDate,
        createDate: existingAssignment?.requestDate || requestDate,
        assignmentRequestId: existingAssignment?.requestId || null,
        assignedHandlerId: existingAssignment?.handlerId || '',
        assignedHandlerName: existingAssignment?.handlerName || '',
        status: rowStatus
      };
    });
  }, [selectedParticipant, allKnownRequests, pendingRotationRequests, allParticipants]);

  // Initialize participant form
  const initializeParticipantForm = (participant = null) => {
    if (participant) {
      // Editing existing participant - populate all current data
      const formData = {
        id: participant.id,
        fullName: participant.fullName || '',
        loftName: participant.loftName || '',
        emailAddress: participant.emailAddress || '',
        mobile: participant.mobile || '',
        role: Array.isArray(participant.role) ? [...participant.role] : (participant.role ? [participant.role] : ['breeder']),
        hideContactInfo: participant.hideContactInfo || false,
        preferredNotificationMethod: participant.preferredNotificationMethod || ['email'],
        handlerInfo: {
          section: participant.handlerInfo?.section || '',
          distance: participant.handlerInfo?.distanceFromRaceStation || participant.handlerInfo?.distance || 0,
          totalBirds: participant.handlerInfo?.totalBirds || 0,
          openSlots: participant.handlerInfo?.openSlots || 0,
          reserved: participant.handlerInfo?.reserved || 0,
          breederApplicationMethod: participant.handlerInfo?.breederApplicationMethod || ['in-app']
        },
        breederInfo: {
          totalBirdsForRace: participant.breederInfo?.totalBirdsForRace || 0,
          handlerName: participant.breederInfo?.handlerName || null,
          assignedBirds: participant.breederInfo?.assignedBirds || []
        }
      };
      setParticipantFormData(formData);
    } else {
      // New participant - start with defaults
      const newFormData = {
        fullName: '',
        loftName: '',
        emailAddress: '',
        mobile: '',
        role: ['breeder'],
        hideContactInfo: false,
        preferredNotificationMethod: ['email'],
        handlerInfo: {
          section: '',
          distance: 0,
          totalBirds: 0,
          openSlots: 0,
          reserved: 0,
          breederApplicationMethod: ['in-app']
        },
        breederInfo: {
          totalBirdsForRace: 0,
          handlerName: null,
          assignedBirds: []
        }
      };
      setParticipantFormData(newFormData);
    }
  };

  const loadRequestsForSelectedParticipant = async (selectedPerson) => {
    if (!selectedPerson) {
      setAdminHandlersRequestsReceived([]);
      return [];
    }

    const { isHandler, isBreeder } = getParticipantRoleFlags(selectedPerson);

    try {
      const requestSources = [];

      if (isBreeder) {
        requestSources.push(getHandlerRequestsByBreederId(selectedPerson.id));
      }

      if (isHandler) {
        requestSources.push(getHandlerRequestsByHandlerId(selectedPerson.id));
      }

      if (requestSources.length === 0) {
        setAdminHandlersRequestsReceived([]);
        return [];
      }

      const results = await Promise.all(requestSources);
      const mergedRequests = results
        .flatMap((result) => (result?.success ? (result.requests || []) : []));

      const seen = new Set();
      const deduplicatedRequests = mergedRequests.filter((request, index) => {
        const key = request?.id
          || request?.requestId
          || `${request?.breederId || 'breeder'}-${request?.handlerId || 'handler'}-${request?.requestDate || 'date'}-${index}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setAdminHandlersRequestsReceived(deduplicatedRequests);
      return deduplicatedRequests;
    } catch (error) {
      setAdminHandlersRequestsReceived([]);
      return [];
    }
  };

  const breederAssignments = useMemo(() => {
    if (!selectedParticipant?.id) return [];
    return adminVisibleHandlerRequests.filter((request) => request?.breederId === selectedParticipant.id);
  }, [adminVisibleHandlerRequests, selectedParticipant]);

  const handlerAssignments = useMemo(() => {
    if (!selectedParticipant?.id) return [];
    return adminVisibleHandlerRequests.filter((request) => request?.handlerId === selectedParticipant.id);
  }, [adminVisibleHandlerRequests, selectedParticipant]);

  const selectedParticipantRoles = useMemo(
    () => (selectedParticipant ? getParticipantRoleFlags(selectedParticipant) : { isBreeder: false, isHandler: false }),
    [selectedParticipant]
  );

  // Handle participant selection
  const handleParticipantSelect = async (participantId) => {
    setSelectedParticipantId(participantId);

    if (participantId === 'new') {
      setIsNewParticipant(true);
      initializeParticipantForm(null);
      setAdminHandlersRequestsReceived([]);
    } else if (participantId) {
      setIsNewParticipant(false);
      const selectedPerson = allParticipants?.find(p => p.id === participantId);
      if (selectedPerson) {
        initializeParticipantForm(selectedPerson);
        await loadRequestsForSelectedParticipant(selectedPerson);
      } else {
        setAdminHandlersRequestsReceived([]);
      }
    } else {
      // Clear selection
      setIsNewParticipant(false);
      setParticipantFormData({});
      setAdminHandlersRequestsReceived([]);
    }
  };

  // Update form field
  const updateFormField = (path, value) => {
    setParticipantFormData(prev => {
      const keys = path.split('.');
      const updated = { ...prev };
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Save participant
  const saveParticipant = async () => {
    if (
      !participantFormData.fullName ||
      (!participantFormData.emailAddress && !participantFormData.mobile)
    ) {
      onMessage('Please fill in required fields (Name and either Email or Mobile)', 'error');
      return;
    }

    try {
      setSaving(true);
      
      let result;
      if (isNewParticipant) {
        result = await createParticipantAsAdmin(participantFormData);
      } else {
        if (!participantFormData.id) {
          onMessage('❌ Error: Participant ID is missing for update', 'error');
          return;
        }
        result = await updateParticipantAsAdmin(participantFormData.id, participantFormData);
      }
      
      if (result.success) {
        const successMessage = isNewParticipant 
          ? `✅ Participant "${participantFormData.fullName}" was successfully added to the system!`
          : `✅ Participant "${participantFormData.fullName}" was successfully updated!`;
        
        onMessage(successMessage, 'success');
        
        // Reload participants first, then reset form
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Reset form after reload
        setSelectedParticipantId('');
        setIsNewParticipant(false);
        setParticipantFormData({});
      } else {
        onMessage(`❌ Failed to ${isNewParticipant ? 'add' : 'update'} participant: ${result.message}`, 'error');
      }
    } catch (error) {
      onMessage('An error occurred while saving participant', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete participant
  const deleteParticipant = async () => {
    if (!participantFormData.id) {
      onMessage('❌ Error: No participant selected for deletion', 'error');
      return;
    }

    try {
      setDeletingParticipant(true);
      setDeleteResult(null);
      
      // Get the current logged in participant's auth token
      const authData = getLoggedInParticipant();
      if (!authData || !authData.authToken) {
        setDeleteResult({ success: false, message: 'Authentication required. Please log in again.' });
        return;
      }
      
      
      const result = await DeleteParticipant(participantFormData.id);
      
      if (result.success) {
        setDeleteResult({ success: true, message: `Participant "${participantFormData.fullName}" was successfully deleted!` });
        
        // Reload participants first, then reset form
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        
        // Also force reload from hook to ensure fresh data
        await loadParticipants(true);
        
        // Reset form after deletion
        setSelectedParticipantId('');
        setIsNewParticipant(false);
        setParticipantFormData({});
      } else {
        setDeleteResult({ success: false, message: `Failed to delete participant: ${result.message}` });
      }
    } catch (error) {
      let errorMessage = 'An error occurred while deleting participant';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setDeleteResult({ success: false, message: errorMessage });
    } finally {
      setDeletingParticipant(false);
    }
  };

  // Close delete modal and reset states
  const closeDeleteModal = () => {
    setShowDeleteConfirm(false);
    setDeleteResult(null);
    setDeletingParticipant(false);
  };

  // Handle assignment
  const refreshAdminData = async () => {
    await loadAdminRotationRequests();
    await loadParticipants(true);
    if (onParticipantUpdate) {
      await onParticipantUpdate();
    }
  };

  const handleSaveBirds = async (requestId, birdDetails) => {
    try {
      setSaving(true);
      const currentRequest = adminVisibleHandlerRequests.find(request => request.id === requestId) || selectedBirdRequest;
      if (!currentRequest) {
        onMessage('Unable to find the selected handler request.', 'error');
        return;
      }

      const result = await updateHandlerRequest(requestId, {
        ...currentRequest,
        birdDetails
      });

      if (result.success) {
        await refreshAdminData();
        if (selectedParticipantId && selectedParticipantId !== 'new') {
          const refreshedSelectedParticipant = allParticipants?.find(p => p.id === selectedParticipantId) || selectedParticipant;
          if (refreshedSelectedParticipant) {
            await loadRequestsForSelectedParticipant(refreshedSelectedParticipant);
          }
        }
        setSelectedBirdRequest({ ...currentRequest, birdDetails });
        setShowBirdInfoModal(false);
        onMessage('Bird details saved successfully.', 'success');
      } else {
        onMessage(`Failed to save bird details: ${result.message}`, 'error');
      }
    } catch (error) {
      onMessage('An error occurred while saving bird details.', 'error');
    } finally {
      setSaving(false);
    }
  };

const handleSaveRequest = async (requestId, draftRequest) => {
  try {
    setSaving(true);

    const currentRequest =
      adminVisibleHandlerRequests.find(r => r.id === requestId) || selectedBirdRequest;

    if (!currentRequest) {
      onMessage('Unable to find the selected handler request.', 'error');
      return { success: false };
    }

    const incomingStatus = normalizeRequestStatus(
      draftRequest?.status
      ?? draftRequest?.requestStatus
      ?? currentRequest?.status
      ?? currentRequest?.requestStatus
      ?? getRequestStatusValue(currentRequest)
      ?? 'pending'
    ) || 'pending';

    const isSoftDelete = incomingStatus === 'deleted';

    let payload;

    if (isSoftDelete) {
      payload = {
        ...currentRequest,
        ...draftRequest,
        id: draftRequest?.id ?? currentRequest?.id ?? requestId,
        breederId: draftRequest?.breederId ?? currentRequest?.breederId,
        handlerId: draftRequest?.handlerId ?? currentRequest?.handlerId,
        birdDetails: Array.isArray(draftRequest?.birdDetails)
          ? draftRequest.birdDetails
          : (Array.isArray(currentRequest?.birdDetails) ? currentRequest.birdDetails : []),
        numberOfBirds: Number(
          draftRequest?.numberOfBirds
          ?? currentRequest?.numberOfBirds
          ?? draftRequest?.birdsRequested
          ?? currentRequest?.birdsRequested
          ?? 0
        ),
        birdsRequested: Number(
          draftRequest?.birdsRequested
          ?? currentRequest?.birdsRequested
          ?? draftRequest?.numberOfBirds
          ?? currentRequest?.numberOfBirds
          ?? 0
        ),
        status: 'deleted'
      };
    } else {
      const normalizedBirdDetails = (Array.isArray(draftRequest?.birdDetails)
        ? draftRequest.birdDetails
        : Array.isArray(currentRequest?.birdDetails)
          ? currentRequest.birdDetails
          : []
      ).map((bird) => {
        const normalizedBirdStatus = normalizeBirdCondition(bird?.birdStatus ?? bird?.status);
        return {
          ...bird,
          sex: normalizeBirdSexOption(
            bird?.sex ?? bird?.Sex ?? bird?.gender ?? bird?.Gender ?? bird?.birdSex ?? bird?.BirdSex
          ),
          birdStatus: normalizedBirdStatus,
          status: normalizedBirdStatus,
        };
      });

      payload = {
        ...currentRequest,
        ...draftRequest,
        id: draftRequest?.id ?? currentRequest?.id ?? requestId,
        birdDetails: normalizedBirdDetails,
        numberOfBirds: Number(draftRequest?.numberOfBirds ?? normalizedBirdDetails.length) || 0,
        birdsRequested: Number(draftRequest?.birdsRequested ?? draftRequest?.numberOfBirds ?? normalizedBirdDetails.length) || 0,
        status: incomingStatus
      };
    }

    const result = await updateHandlerRequest(requestId, payload);

    if (result.success) {
      await refreshAdminData();

      if (selectedParticipantId && selectedParticipantId !== 'new') {
        const refreshed = allParticipants?.find(p => p.id === selectedParticipantId) || selectedParticipant;
        if (refreshed) {
          await loadRequestsForSelectedParticipant(refreshed);
        }
      }

      setSelectedBirdRequest(payload);
      setShowBirdInfoModal(false);
      onMessage(
        isSoftDelete
          ? 'Handler request deleted successfully.'
          : 'Handler request updated successfully.',
        'success'
      );

      return { success: true };
    } else {
      onMessage(`Failed to update handler request: ${result.message}`, 'error');
      return { success: false, message: result.message };
    }
  } catch (error) {
    onMessage('An error occurred while saving the handler request.', 'error');
    return { success: false, message: error?.message };
  } finally {
    setSaving(false);
  }
};
  const handleAssignHandler = async ({ requestId, breederId, handlerId, birdsToAssign, existingAssignmentRequestId = null }) => {
    try {
      setSaving(true);
      const rotationRequest = requestId ? pendingRotationRequests.find(request => request.id === requestId) : null;
      const targetBreederId = rotationRequest?.breederId || breederId;

      if (!targetBreederId) {
        onMessage('Unable to locate the selected breeder for this rotation assignment.', 'error');
        return;
      }

      if (existingAssignmentRequestId) {
        const existingRequest = allKnownRequests.find((request) => {
          const requestIdValue = request?.id || request?.requestId || request?.RequestId;
          return String(requestIdValue || '') === String(existingAssignmentRequestId);
        });

        if (!existingRequest) {
          onMessage('Unable to locate the existing rotation assignment request.', 'error');
          return;
        }

        const birdsInExistingRequest = Number(existingRequest?.numberOfBirds || existingRequest?.birdsRequested || birdsToAssign || 1) || 1;

        const reassignResult = await updateHandlerRequest(existingAssignmentRequestId, {
          ...existingRequest,
          breederId: targetBreederId,
          handlerId,
          numberOfBirds: birdsInExistingRequest,
          birdsRequested: birdsInExistingRequest,
          status: 'approved',
          requestNote: 'admin-rotation-assigned',
          isMandatoryRotation: true,
          IsMandatoryRotation: true
        });

        if (!reassignResult.success) {
          onMessage(`Failed to reassign rotation bird: ${reassignResult.message}`, 'error');
          return;
        }

        await refreshAdminData();
        if (selectedParticipantId && selectedParticipantId !== 'new') {
          const refreshedSelectedParticipant = allParticipants?.find(p => p.id === selectedParticipantId) || selectedParticipant;
          if (refreshedSelectedParticipant) {
            await loadRequestsForSelectedParticipant(refreshedSelectedParticipant);
          }
        }

        onMessage('Rotation bird handler updated successfully.', 'success');
        return;
      }

      const requestPayload = buildMandatoryRotationHandlerRequest(
        rotationRequest || { breederId: targetBreederId },
        handlerId,
        birdsToAssign
      );
      const createResult = await sendHandlerRequest(targetBreederId, handlerId, requestPayload);

      if (!createResult.success) {
        onMessage(`Failed to create handler request: ${createResult.message}`, 'error');
        return;
      }

      const createdRequest = createResult.request || createResult?.data?.request || null;
      const createdRequestId = createdRequest?.id || createResult?.requestId || createResult?.id;

      if (createdRequestId) {
        const forceApprovedResult = await updateHandlerRequest(createdRequestId, {
          ...requestPayload,
          ...createdRequest,
          id: createdRequestId,
          status: 'approved',
          requestNote: 'admin-rotation-assigned',
          isMandatoryRotation: true,
          IsMandatoryRotation: true
        });

        if (!forceApprovedResult.success) {
          onMessage(`Assignment created, but status sync failed: ${forceApprovedResult.message}`, 'error');
          return;
        }
      }

      if (rotationRequest) {
        const totalBirds = Number(rotationRequest.numberOfBirds || rotationRequest.birdsRequested || 0);
        const alreadyAssigned = Number(rotationRequest.assignedBirds || 0);
        const assignedBirds = Math.min(totalBirds, alreadyAssigned + birdsToAssign);
        const remainingBirds = Math.max(0, totalBirds - assignedBirds);

        const updateRotationResult = await updateHandlerRequest(rotationRequest.id, {
          ...rotationRequest,
          assignedBirds,
          status: remainingBirds === 0 ? 'approved' : (rotationRequest.status || 'pending')
        });

        if (!updateRotationResult.success) {
          onMessage(`Handler request created, but rotation request sync failed: ${updateRotationResult.message}`, 'error');
          return;
        }
      }

      await refreshAdminData();
      if (selectedParticipantId && selectedParticipantId !== 'new') {
        const refreshedSelectedParticipant = allParticipants?.find(p => p.id === selectedParticipantId) || selectedParticipant;
        if (refreshedSelectedParticipant) {
          await loadRequestsForSelectedParticipant(refreshedSelectedParticipant);
        }
      }
      onMessage('Rotation bird assigned and auto-approved successfully.', 'success');
    } catch (error) {
      onMessage('An error occurred while assigning handler', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!hasRole('admin')) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        You need to have the Admin role to access this section. Admin roles are assigned by system administrators.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {selectedParticipantId && selectedParticipantId !== 'new' && selectedParticipant && (
        <div className="card border-0 shadow-sm" style={{ backgroundColor: '#eef6ff' }}>
          <div className="card-body py-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="badge" style={{ backgroundColor: '#0b3d91' }}>ADMIN VIEW</span>
                  <span className="text-muted small">Managing Participant</span>
                </div>
                <h6 className="mb-1 fw-bold">
                  Currently managing: {selectedParticipant.fullName}
                  {selectedParticipant.loftName ? <span className="text-muted"> ({selectedParticipant.loftName})</span> : null}
                </h6>
                <div className="small text-muted d-flex flex-wrap align-items-center gap-2">
                  <span>Roles:</span>
                  {(Array.isArray(selectedParticipant.role) ? selectedParticipant.role : [selectedParticipant.role])
                    .filter(Boolean)
                    .map((role) => (
                      <span key={role} className="badge bg-light text-dark border">
                        {String(role).charAt(0).toUpperCase() + String(role).slice(1)}
                      </span>
                    ))}
                  {selectedParticipant.emailAddress && (
                    <>
                      <span className="mx-1">•</span>
                      <span>{selectedParticipant.emailAddress}</span>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleParticipantSelect('')}
                disabled={saving}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Rotation Requests */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-arrow-repeat me-2"></i>
            Rotation Birds ({selectedParticipantRotationBirdRows.length})
          </h5>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={loadAdminRotationRequests}
            disabled={loadingRequests}
          >
            <i className={`bi ${loadingRequests ? 'bi-arrow-clockwise' : 'bi-arrow-clockwise'} me-1`}></i>
            {loadingRequests ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body">
          {loadingRequests ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading rotation requests...</p>
            </div>
          ) : !selectedParticipantId || selectedParticipantId === 'new' ? (
            <div className="text-center text-muted py-4">
              <i className="bi bi-person-lines-fill display-4 text-muted"></i>
              <p className="mt-2">Select a breeder in Participant Management to view rotation birds.</p>
            </div>
          ) : !selectedParticipant || !getParticipantRoleFlags(selectedParticipant).isBreeder ? (
            <div className="text-center text-muted py-4">
              <i className="bi bi-person-badge display-4 text-muted"></i>
              <p className="mt-2">Rotation birds are shown only when a breeder is selected.</p>
            </div>
          ) : selectedParticipantRotationBirdRows.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <p className="mt-2">No rotation birds required for this breeder</p>
              <small>Rotation bird count is based on the breeder rotation rule.</small>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Rotation Bird</th>
                      <th>Breeder</th>
                      <th>Assign Handler</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedParticipantRotationBirdRows.map((request) => {
                      const isAssigned = request.status === 'approved' && Boolean(request.assignedHandlerId);
                      const selectedHandlerId = selectedRotationBirdHandlers[request.rowId] || request.assignedHandlerId || '';
                      const selectedHandler = availableHandlers.find(handler => handler.id === selectedHandlerId);
                      const capacitySummary = selectedHandler ? getHandlerCapacitySummary(selectedHandler, allKnownRequests) : null;
                      const slotsForThisBreeder = selectedHandler ? calculateAvailableSlotsForBreeder(selectedHandler, request.breederId, allKnownRequests) : 0;

                      return (
                      <tr key={request.rowId}>
                        <td>
                          <div>
                            <strong>Bird {request.rotationBirdNumber}</strong>
                            <div className="small text-muted">
                              Rotation rule: {request.requiredRotationBirds} required from {request.totalBirds} total birds
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>
                              {request.breederName || request.participantName || 'Unknown Breeder'}
                              {(request.breederLoftName || request.loftName) && (
                                <span className="text-muted"> ({request.breederLoftName || request.loftName})</span>
                              )}
                            </strong>
                            <div className="small text-muted">
                              <i className="bi bi-envelope me-1"></i>
                              {request.breederEmail || request.email || 'No email'}
                            </div>
                            {(request.breederMobile || request.mobile) && (
                              <div className="small text-muted">
                                <i className="bi bi-telephone me-1"></i>
                                {request.breederMobile || request.mobile}
                              </div>
                            )}

                          </div>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={selectedHandlerId}
                            onChange={(event) => {
                              const nextHandlerId = event.target.value;
                              setSelectedRotationBirdHandlers(prev => ({
                                ...prev,
                                [request.rowId]: nextHandlerId
                              }));
                            }}
                            disabled={!isEditing || saving}
                          >
                            <option value="">Select handler...</option>
                            {sortedHandlersForRotation.map((handler) => {
                              const handlerCapacitySummary = getHandlerCapacitySummary(handler, allKnownRequests);
                              const breederSlots = calculateAvailableSlotsForBreeder(handler, request.breederId, allKnownRequests);
                              const section = handler.handlerInfo?.section || 'N/A';
                              const utilizationPercent = handlerCapacitySummary.totalBirds > 0
                                ? Math.round((handlerCapacitySummary.approved / handlerCapacitySummary.totalBirds) * 100)
                                : 100;

                              return (
                                <option
                                  key={handler.id}
                                  value={handler.id}
                                  disabled={handlerCapacitySummary.isAtCapacity || breederSlots <= 0}
                                >
                                  {handler.fullName}{handler.loftName ? ` (${handler.loftName})` : ''} - Sec: {section} - Load: {utilizationPercent}% ({handlerCapacitySummary.approved}/{handlerCapacitySummary.totalBirds}){handlerCapacitySummary.isAtCapacity ? ' - FULL' : ''}
                                </option>
                              );
                            })}
                          </select>
                          {selectedHandler && (
                            <div className="small mt-2">
                              <div className="text-muted">
                                <strong>{selectedHandler.fullName}</strong>
                                {selectedHandler.loftName ? ` (${selectedHandler.loftName})` : ''}
                              </div>
                              <div className="text-muted">
                                Section: {selectedHandler.handlerInfo?.section || 'N/A'} • Capacity used: {capacitySummary?.approved || 0}/{capacitySummary?.totalBirds || 0}
                              </div>
                              <div className={`${slotsForThisBreeder > 0 ? 'text-success' : 'text-danger'}`}>
                                Breeder limit remaining: {slotsForThisBreeder}
                              </div>
                            </div>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(request.requestDate || request.createDate).toLocaleDateString()}
                          </small>
                        </td>
                        <td>
                          {request.status === 'approved' ? (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle me-1"></i>
                              Assigned / Approved
                            </span>
                          ) : request.status === 'pending' ? (
                            <span className="badge bg-info text-dark">
                              <i className="bi bi-hourglass-split me-1"></i>
                              Pending Approval
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-clock me-1"></i>
                              Awaiting Assignment
                            </span>
                          )}
                          {request.assignedHandlerName && (
                            <div className="small text-muted mt-1">
                              {request.assignedHandlerName}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-2">
                            {isEditing ? (
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={async () => {
                                  if (!selectedHandlerId) {
                                    onMessage('Please select a handler for this rotation bird.', 'error');
                                    return;
                                  }

                                  await handleAssignHandler({
                                    requestId: request.sourceRotationRequestId,
                                    breederId: request.breederId,
                                    handlerId: selectedHandlerId,
                                    birdsToAssign: 1,
                                    existingAssignmentRequestId: request.assignmentRequestId
                                  });

                                  setSelectedRotationBirdHandlers(prev => {
                                    const next = { ...prev };
                                    delete next[request.rowId];
                                    return next;
                                  });
                                }}
                                disabled={saving || !selectedHandlerId || (isAssigned && selectedHandlerId === request.assignedHandlerId)}
                              >
                                <i className="bi bi-person-plus me-1"></i>
                                {isAssigned ? 'Update Handler' : 'Assign Bird'}
                              </button>
                            ) : (
                              <span className="text-muted small">
                                Enable edit mode to assign handlers
                              </span>
                            )}
                            {(() => {
                              const editRequestId = request.assignmentRequestId || request.sourceRotationRequestId;
                              const editRequest = editRequestId
                                ? allKnownRequests.find(r => r.id === editRequestId)
                                : null;
                              return editRequest ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => {
                                    const breeder = allParticipants?.find(p => p.id === (editRequest.breederId || request.breederId));
                                    setSelectedBirdRequest({
                                      ...editRequest,
                                      breederName: breeder?.fullName || editRequest.breederName || request.breederName || 'Unknown Breeder',
                                      breederLoftName: breeder?.loftName || editRequest.breederLoftName || request.breederLoftName || '',
                                    });
                                    setShowBirdInfoModal(true);
                                  }}
                                  disabled={saving}
                                >
                                  <i className="bi bi-pencil-square me-1"></i>
                                  Edit Bird Info
                                </button>
                              ) : null;
                            })()}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>

              {availableHandlers.length === 0 && isEditing && (
                <div className="alert alert-warning mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>No available handlers</strong> - All handlers are at capacity. You may need to increase handler limits or add new handlers.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Handler Requests */}
      {selectedParticipantId
        && selectedParticipantId !== 'new'
        && ((selectedParticipantRoles.isBreeder && breederAssignments.length > 0)
          || (selectedParticipantRoles.isHandler && handlerAssignments.length > 0)) && (
        <div className="card shadow-sm">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0" style={{ color: '#0b3d91' }}>
              <i className="bi bi-list-check me-2"></i>
              {selectedParticipantRequestLabel}
            </h5>
          </div>
          <div className="card-body">
            {selectedParticipantRoles.isBreeder && breederAssignments.length > 0 && (
              <div className="mb-4">
                <h6 className="fw-bold mb-1">My Birds Assigned to Handlers ({breederAssignments.length})</h6>
                <div className="text-muted small mb-3">Birds you own that are currently assigned to other handlers</div>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Breeder</th>
                        <th>Handler</th>
                        <th>Birds</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breederAssignments.map((request) => {
                        return (
                          <tr key={`breeder-${request.id}`}>
                            <td>
                              <strong>
                                {request.breederName}
                                {request.breederLoftName ? <span className="text-muted"> ({request.breederLoftName})</span> : null}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                {request.handlerName}
                                {request.handlerLoftName ? <span className="text-muted"> ({request.handlerLoftName})</span> : null}
                              </strong>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                {request.numberOfBirds || request.birdsRequested || 0} {(request.numberOfBirds || request.birdsRequested || 0) === 1 ? 'Bird' : 'Birds'}
                              </span>
                            </td>
                            <td>
                              {request.isMandatoryRotation ? (
                                <span className="badge bg-primary">Mandatory Rotation</span>
                              ) : (
                                <span className="badge bg-secondary">Standard</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${String(request.status).toLowerCase() === 'approved' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {String(request.status || 'pending').charAt(0).toUpperCase() + String(request.status || 'pending').slice(1)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(request.requestDate).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => {
                                    setSelectedBirdRequest(request);
                                    setShowBirdInfoModal(true);
                                  }}
                                  disabled={saving}
                                >
                                  <i className="bi bi-pencil-square me-1"></i>
                                  Edit Bird Info
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedParticipantRoles.isHandler && handlerAssignments.length > 0 && (
              <div>
                <h6 className="fw-bold mb-1">Birds I’m Handling ({handlerAssignments.length})</h6>
                <div className="text-muted small mb-3">Birds from other breeders that you are currently handling</div>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Breeder</th>
                        <th>Handler</th>
                        <th>Birds</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {handlerAssignments.map((request) => {
                        return (
                          <tr key={`handler-${request.id}`}>
                            <td>
                              <strong>
                                {request.breederName}
                                {request.breederLoftName ? <span className="text-muted"> ({request.breederLoftName})</span> : null}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                {request.handlerName}
                                {request.handlerLoftName ? <span className="text-muted"> ({request.handlerLoftName})</span> : null}
                              </strong>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                {request.numberOfBirds || request.birdsRequested || 0} {(request.numberOfBirds || request.birdsRequested || 0) === 1 ? 'Bird' : 'Birds'}
                              </span>
                            </td>
                            <td>
                              {request.isMandatoryRotation ? (
                                <span className="badge bg-primary">Mandatory Rotation</span>
                              ) : (
                                <span className="badge bg-secondary">Standard</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${String(request.status).toLowerCase() === 'approved' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {String(request.status || 'pending').charAt(0).toUpperCase() + String(request.status || 'pending').slice(1)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(request.requestDate).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => {
                                    setSelectedBirdRequest(request);
                                    setShowBirdInfoModal(true);
                                  }}
                                  disabled={saving}
                                >
                                  <i className="bi bi-pencil-square me-1"></i>
                                  Edit Bird Info
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Participant Management */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-person-gear me-2"></i>
            Participant Management
          </h5>
        </div>
        <div className="card-body">
          {isEditing ? (
            <>
              {/* Participant Selection */}
              <div className="mb-3">
                <label className="form-label fw-bold">Select Participant</label>
                <select 
                  className="form-select"
                  value={selectedParticipantId}
                  onChange={(e) => handleParticipantSelect(e.target.value)}
                >
                  <option value="">-- Select a participant to edit --</option>
                  <option value="new">🆕 Add New Participant</option>
                  <optgroup label="Existing Participants">
                    {allParticipants?.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.fullName} {p.loftName && `(${p.loftName})`} - {Array.isArray(p.role) ? p.role.join(', ') : p.role}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Participant Form */}
              {selectedParticipantId && (
                <div className="border rounded p-3 bg-light">
                  <h6 className="fw-bold mb-3">
                    {isNewParticipant ? (
                      <><i className="bi bi-person-plus me-1"></i>Add New Participant</>
                    ) : (
                      <><i className="bi bi-pencil me-1"></i>Edit: {participantFormData.fullName || 'Loading...'}</>
                    )}
                  </h6>
                  {process.env.NODE_ENV === 'development' && (
                    <details className="mb-3">
                      <summary className="text-muted small" style={{cursor: 'pointer'}}>Debug: Current Form Data</summary>
                      <pre className="small text-muted mt-1" style={{fontSize: '10px'}}>
                        {JSON.stringify(participantFormData, null, 2)}
                      </pre>
                    </details>
                  )}

                  {/* Personal Info */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Full Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={participantFormData.fullName || ''}
                        onChange={(e) => updateFormField('fullName', e.target.value)}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Loft Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={participantFormData.loftName || ''}
                        onChange={(e) => updateFormField('loftName', e.target.value)}
                        placeholder="Enter loft name"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Email <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        value={participantFormData.emailAddress || ''}
                        onChange={(e) => updateFormField('emailAddress', e.target.value)}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Mobile Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={participantFormData.mobile || ''}
                        onChange={(e) => updateFormField('mobile', e.target.value)}
                        placeholder="Enter mobile number"
                      />
                    </div>
                  </div>

                  {/* Roles */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">Roles</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['breeder', 'handler', 'admin'].map(role => {
                        const isSelected = participantFormData.role?.includes(role.toLowerCase());
                        return (
                          <button
                            key={role}
                            type="button"
                            className={`btn btn-sm ${
                              isSelected 
                                ? (role === 'admin' ? 'btn-danger' : role === 'handler' ? 'btn-success' : 'btn-primary')
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() => {
                              const currentRoles = participantFormData.role || [];
                              if (isSelected) {
                                updateFormField('role', currentRoles.filter(r => r !== role));
                              } else {
                                updateFormField('role', [...currentRoles, role]);
                              }
                            }}
                          >
                            {isSelected && <i className="bi bi-check me-1"></i>}
                            {role.charAt(0).toUpperCase() + role.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Handler Info */}
                  {participantFormData.role?.includes('handler') && (
                    <div className="border rounded p-2 mb-3 bg-white">
                      <h6 className="fw-bold text-success mb-2"><i className="bi bi-hand-thumbs-up me-1"></i>Handler Information</h6>
                      <div className="row mb-2">
                        <div className="col-md-4">
                          <label className="form-label">Section</label>
                          <select
                            className="form-select form-select-sm"
                            value={participantFormData.handlerInfo?.section || ''}
                            onChange={(e) => updateFormField('handlerInfo.section', e.target.value)}
                          >
                            <option value="">Select Section...</option>
                            <option value="NORTH">NORTH</option>
                            <option value="CENTRAL">CENTRAL</option>
                            <option value="SOUTH">SOUTH</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Distance (miles)</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.handlerInfo?.distance || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              updateFormField('handlerInfo.distance', value);
                              updateFormField('handlerInfo.distanceFromRaceStation', value);
                            }}
                            min="0"
                            step="0.1"
                            placeholder="Distance from liberation point"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Total Capacity</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.handlerInfo?.totalBirds || ''}
                            onChange={(e) => updateFormField('handlerInfo.totalBirds', parseInt(e.target.value) || 0)}
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-4">
                          <label className="form-label">Open Slots</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={openSlots || ''}
                            onChange={(e) => updateFormField('handlerInfo.openSlots', parseInt(e.target.value) || 0)}
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Breeder Application Method</label>
                          <div className="form-check mt-1">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="requireCall"
                              checked={participantFormData.handlerInfo?.breederApplicationMethod?.includes('call') || false}
                              onChange={(e) => {
                                const currentMethods = participantFormData.handlerInfo?.breederApplicationMethod || ['in-app'];
                                const newMethods = e.target.checked 
                                  ? [...currentMethods.filter(m => m !== 'call'), 'call']
                                  : currentMethods.filter(m => m !== 'call');
                                // Ensure in-app is always included
                                const finalMethods = newMethods.includes('in-app') ? newMethods : ['in-app', ...newMethods];
                                updateFormField('handlerInfo.breederApplicationMethod', finalMethods);
                              }}
                            />
                            <label className="form-check-label" htmlFor="requireCall">
                              Require Call (breeders must call first)
                            </label>
                          </div>
                          <small className="text-muted">In-app requests are always enabled</small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Privacy & Notification Settings */}
                  <div className="border rounded p-2 mb-3 bg-white">
                    <h6 className="fw-bold text-info mb-2"><i className="bi bi-gear me-1"></i>Privacy & Notification Settings</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Preferred Notification Method</label>
                        <div className="d-flex flex-wrap gap-2">
                          {['email', 'sms', 'phone'].map(method => {
                            const isSelected = participantFormData.preferredNotificationMethod?.includes(method);
                            return (
                              <button
                                key={method}
                                type="button"
                                className={`btn btn-sm ${isSelected ? 'btn-info' : 'btn-outline-secondary'}`}
                                onClick={() => {
                                  const current = participantFormData.preferredNotificationMethod || [];
                                  if (isSelected) {
                                    updateFormField('preferredNotificationMethod', current.filter(m => m !== method));
                                  } else {
                                    updateFormField('preferredNotificationMethod', [...current, method]);
                                  }
                                }}
                              >
                                {isSelected && <i className="bi bi-check me-1"></i>}
                                <i className={`bi ${method === 'email' ? 'bi-envelope' : method === 'sms' ? 'bi-chat-dots' : 'bi-telephone'} me-1`}></i>
                                {method.charAt(0).toUpperCase() + method.slice(1)}
                              </button>
                            );
                          })}
                        </div>
                        <small className="text-muted">Select one or more notification methods</small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Privacy Settings</label>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="hideContactInfo"
                            checked={participantFormData.hideContactInfo || false}
                            onChange={(e) => updateFormField('hideContactInfo', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="hideContactInfo">
                            <i className="bi bi-eye-slash me-1"></i>
                            Hide my contact information from other participants
                          </label>
                        </div>
                        <small className="text-muted">When checked, your email and phone won't be visible to other participants</small>
                      </div>
                    </div>
                  </div>

                  {/* Breeder Info */}
                  {participantFormData.role?.includes('breeder') && (
                    <div className="border rounded p-2 mb-3 bg-white">
                      <h6 className="fw-bold text-primary mb-2"><i className="bi bi-egg me-1"></i>Breeder Information</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">Total Birds for Race</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={participantFormData.breederInfo?.totalBirdsForRace || ''}
                            onChange={(e) => updateFormField('breederInfo.totalBirdsForRace', parseInt(e.target.value) || 0)}
                            min="0"
                            placeholder="Number of birds to enter in race"
                          />
                        </div>
                      </div>
                      {!isNewParticipant && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Current Status: {participantFormData.breederInfo?.assignedBirds?.length || 0} birds assigned
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="d-flex justify-content-end align-items-center">
                    {!isNewParticipant && (
                      <button
                        type="button"
                        className="btn btn-danger me-2"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={saving || deletingParticipant}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete
                      </button>
                    )}
                    {isNewParticipant && (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={saveParticipant}
                        disabled={
                          saving ||
                          !participantFormData.fullName ||
                          (!participantFormData.emailAddress && !participantFormData.mobile)
                        }
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-1"></i>
                            Add Participant
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">
              <i className="bi bi-lock display-4 text-muted"></i>
              <p className="mt-2">Enable edit mode to manage participants</p>
            </div>
          )}
        </div>
      </div>

      {showBirdInfoModal && selectedBirdRequest && (
        <AdminBirdInfoModal
          show={showBirdInfoModal}
          request={selectedBirdRequest}
          onSaveBirds={handleSaveBirds}
          onSaveRequest={handleSaveRequest}
          availableHandlers={availableHandlers}
          onClose={() => {
            setShowBirdInfoModal(false);
            setSelectedBirdRequest(null);
          }}
          getHandlerName={getHandlerDisplayName}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Delete Participant
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeDeleteModal}
                  disabled={deletingParticipant && !deleteResult}
                ></button>
              </div>
              <div className="modal-body">
                {deleteResult ? (
                  // Show result after operation
                  <div className={`alert ${deleteResult.success ? 'alert-success' : 'alert-danger'}`}>
                    <i className={`bi ${deleteResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                    <strong>{deleteResult.success ? 'Success!' : 'Error!'}</strong>
                    <p className="mb-0 mt-2">{deleteResult.message}</p>
                  </div>
                ) : (
                  // Show confirmation before operation
                  <>
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Warning:</strong> This action cannot be undone!
                    </div>
                    <p>
                      Are you sure you want to permanently delete <strong>{participantFormData.fullName}</strong>?
                    </p>
                    <p className="text-muted mb-0">
                      This will remove all of their information, bird assignments, and handler relationships from the system.
                    </p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {deleteResult ? (
                  // Show close button after operation
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={closeDeleteModal}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Close
                  </button>
                ) : (
                  // Show cancel/delete buttons before operation
                  <>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={closeDeleteModal}
                      disabled={deletingParticipant}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={deleteParticipant}
                      disabled={deletingParticipant}
                    >
                      {deletingParticipant ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-trash me-1"></i>
                          Yes, Delete Participant
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
